import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Analyse } from '../domain/Analyse/analyse';
import { AnalyseStatut } from '../domain/Analyse/enum/analyse-statut';
import { EReportCreationType } from '../domain/Analyse/enum/report-type';
import { CoreData } from '../tools/core-data';
import { LoadingTask } from '../tools/loading-task';
import { BaseService } from './base-service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IBMRAnalyse } from '../domain/IBMR/IBMR-analyse';

@Injectable({
    providedIn: 'root',
})
export class AnalyseService extends BaseService {

    private serviceUrl = `${environment.serviceMainBaseUrl}api/analyse`;

    constructor(
        protected http: HttpClient,
        protected coreData: CoreData,
        protected loadingTask: LoadingTask,
        protected snackBare: MatSnackBar,
        protected router: Router,
        ) {
            super(coreData, router, snackBare);
    }

    getAnalyseIBMRByCode(interventionCode: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/intervention/${interventionCode}/IBMR`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAnalyseIBMRByCode')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: IBMRAnalyse) => {
                return IBMRAnalyse.mapFromJson(response);
            })
        );
    }

    getAnalyseByIntervention(interventionCode: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/intervention/${interventionCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAnalyseByIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Analyse) => {
                return Analyse.mapFromJson(response);
            })
        );
    }

    getAnalysisListByStatus(status: AnalyseStatut) {
        this.loadingTask.activateLoading();
        let params = new HttpParams();
        if (status) {
            params = params.append('status', `${status}`);
        }

        return this.http.get(`${this.serviceUrl}`, this.generateHeaderWithParameters(params)).pipe(
            catchError(this.handleError<any>('getAllAnalysis')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Analyse[]) => {
                return response.map((item: Analyse) => {
                    return Analyse.mapFromJson(item);
                });
            })
        );
    }

    getAnalyseBySampleCode(sampleCode: string): Observable<Analyse> {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/BySampleCode/${sampleCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAllAnalysis')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Analyse) => {
                return Analyse.mapFromJson(response);
            })
        );
    }

    update(analysis: Analyse | IBMRAnalyse) {
        this.loadingTask.activateLoading();
        let body: any;
        body = Object.assign({}, analysis);
        body.Analystes = analysis.AnalyserList.map(x => x.Code);
        if (analysis instanceof Analyse) {
            body.Trieurs = analysis.SorterList.map(x => x.Code);
        }
        return this.http.put(`${this.serviceUrl}/${analysis.Code}`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('updateAnalysis')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    // generateExport(interventionCode: string, type: EDocumentType, reason?: string) {
    //     this.loadingTask.activateLoading();
    //     let body: any = {};
    //     if (reason) {
    //         body.reason = reason;
    //     }
    //     switch (type)
    //     {
    //         case EDocumentType.SaisieMinvCE:
    //             var uriType = "MinvCE";
    //             break;
    //     }
    //     return this.http.post(`${this.serviceUrl}/${interventionCode}/${uriType}`, body, this.generateHeader()).pipe(
    //         catchError(this.handleError<any>('generateIntervention')),
    //         finalize(() => this.loadingTask.deactivateLoading()),
    //         map((response: Analyse) => {
    //             return Analyse.mapFromJson(response);
    //         })
    //     );
    // }

    generateReport(interventionCode: string, type: EReportCreationType, reason?: string) {
        this.loadingTask.activateLoading();
        const body: any = {};
        if (reason) {
            body.reason = reason;
        }
        return this.http.post(`${this.serviceUrl}/${interventionCode}/report/${type}`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('generateIntervention')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: boolean | string) => {
                return response;
            })
        );
    }

    finishAnalyse(analyseCode: string) {
        this.loadingTask.activateLoading();
        return this.http.put(`${this.serviceUrl}/${analyseCode}/Finish`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('analyseFinished')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    getAnalyseByCode(analysisCode: string) {
        this.loadingTask.activateLoading();
        return this.http.get(`${this.serviceUrl}/${analysisCode}`, this.generateHeader()).pipe(
            catchError(this.handleError<any>('getAnalysisByCode')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map((response: Analyse) => {
                return Analyse.mapFromJson(response);
            })
        );
    }

    sendInterpretation(analyse: Analyse) {
        this.loadingTask.activateLoading();
        const body: any = {};
        body.interpretation = analyse.Interpretation;
        return this.http.put(`${this.serviceUrl}/${analyse.Code}/Interpretation`, body, this.generateHeader()).pipe(
            catchError(this.handleError<any>('sendInterpretation')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }

    reset(analysisCode: string) {
        this.loadingTask.activateLoading();

        return this.http.put(`${this.serviceUrl}/${analysisCode}/reset`, null, this.generateHeader()).pipe(
            catchError(this.handleError<any>('reset')),
            finalize(() => this.loadingTask.deactivateLoading()),
            map(x => x));
    }
}
